import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock3, Send, Loader2, ExternalLink } from "lucide-react";
import { Reveal } from "./Reveal";
import { LogoMark } from "./Logo";
import { BRAND, API } from "../constants/brand";

export const Footer = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Bitte alle Felder ausfüllen.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name, email, message });
      toast.success("Nachricht gesendet — wir melden uns schnellstmöglich.");
      setName("");
      setEmail("");
      setMessage("");
    } catch {
      toast.error("Nachricht konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="kontakt" className="relative bg-[#080b12] border-t border-white/5 overflow-hidden">
      <div className="pointer-events-none select-none absolute -bottom-10 left-1/2 -translate-x-1/2 text-[22vw] font-black tracking-tighter text-white/[0.025] leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
        M-LINE
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="flex items-center gap-4">
            <LogoMark size={48} />
            <div>
              <div className="text-xl font-bold text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>
                M-Line <span className="text-gradient-blue">Solutions</span>
              </div>
              <div className="text-[10px] tracking-[0.3em] text-slate-500 font-mono-tech mt-1">{BRAND.tagline}</div>
            </div>
          </div>
          <p className="mt-6 text-sm text-slate-400 leading-relaxed max-w-sm">
            Gütertransport, Werkstatt-Expertise und Geschenkkarten — alles unter einem Dach in Karlsruhe.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-3 text-slate-300 hover:text-cyan-300 transition-colors">
              <Phone size={15} className="text-cyan-400" /> <span className="font-mono-tech">{BRAND.phone}</span>
            </a>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-slate-300 hover:text-cyan-300 transition-colors">
              <Mail size={15} className="text-cyan-400" /> <span className="font-mono-tech">{BRAND.email}</span>
            </a>
            <a href={`mailto:${BRAND.emailInfo}`} className="flex items-center gap-3 text-slate-300 hover:text-cyan-300 transition-colors">
              <Mail size={15} className="text-cyan-400" /> <span className="font-mono-tech">{BRAND.emailInfo}</span>
            </a>
            <div className="flex items-center gap-3 text-slate-300">
              <MapPin size={15} className="text-cyan-400" /> {BRAND.address}
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <Clock3 size={15} className="text-cyan-400" /> Mo–Fr 08:00–18:00 · Sa 09:00–14:00
            </div>
          </div>
          <a
            data-testid="footer-partner-badge"
            href="https://www.carboncleangermany.de"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 max-w-sm hover:border-cyan-400/40 transition-colors group"
          >
            <img
              src="/partner-carbonclean.jpg"
              alt="CarbonCleanGermany — Motor Carbon Clean Logo"
              className="w-14 h-14 rounded-xl object-cover shrink-0"
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 font-mono-tech">Unser Partner</div>
              <div className="text-sm font-semibold text-slate-100 group-hover:text-cyan-300 transition-colors flex items-center gap-1.5">
                CarbonCleanGermany <ExternalLink size={12} className="text-cyan-400" />
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                carboncleangermany.de — Spezialist für Wasserstoff-Motorreinigung
              </div>
            </div>
          </a>
        </Reveal>

        <Reveal className="lg:col-span-4" delay={0.1}>
          <h4 className="text-lg font-semibold text-slate-50 mb-5">Schreib uns direkt</h4>
          <div className="space-y-3">
            <input
              data-testid="contact-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dein Name"
              className="w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
            />
            <input
              data-testid="contact-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-Mail-Adresse"
              className="w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
            />
            <textarea
              data-testid="contact-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Worum geht es? Fahrt, Fahrzeug oder Werkstatt…"
              rows={4}
              className="w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50 resize-none"
            />
            <button
              data-testid="contact-form-submit-button"
              onClick={submit}
              disabled={sending}
              className="w-full rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] py-3 text-sm font-bold text-white flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Nachricht senden
            </button>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-3" delay={0.2}>
          <h4 className="text-lg font-semibold text-slate-50 mb-5">Unser Standort</h4>
          <div className="rounded-2xl overflow-hidden border border-white/10 glow-border">
            <iframe
              data-testid="footer-standort-map"
              title="M-Line Solutions Standort — Babbergerstr. 3, 76189 Karlsruhe"
              src="https://www.openstreetmap.org/export/embed.html?bbox=8.3103%2C49.0005%2C8.3303%2C49.0105&layer=mapnik&marker=49.0055038%2C8.3203056"
              className="w-full aspect-video border-0"
              loading="lazy"
            />
          </div>
          <a
            data-testid="footer-route-link"
            href="https://www.google.com/maps/dir/?api=1&destination=Babbergerstra%C3%9Fe+3%2C+76189+Karlsruhe"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <MapPin size={13} /> Route zu uns planen → Babbergerstr. 3, 76189 Karlsruhe
          </a>
          <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 font-mono-tech mb-1">Treuebonus</div>
            <p className="text-xs text-slate-300 leading-relaxed">
              Jeder Transport zählt: Nach 10 gebuchten Transporten ist dein <span className="text-cyan-300 font-semibold">11. Transport kostenlos</span>.
            </p>
          </div>
        </Reveal>
      </div>

      <div className="relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
          <span>© 2026 M-Line Solutions · Karlsruhe</span>
          <div className="flex gap-6">
            <a href="/verfolgung" data-testid="footer-link-verfolgung" className="hover:text-slate-400 transition-colors">Sendungsverfolgung</a>
            <a href="/geschaeftskunden" data-testid="footer-link-b2b" className="hover:text-slate-400 transition-colors">Für Geschäftskunden</a>
            <a href="/impressum" data-testid="footer-link-impressum" className="hover:text-slate-400 transition-colors">Impressum</a>
            <a href="/datenschutz" data-testid="footer-link-datenschutz" className="hover:text-slate-400 transition-colors">Datenschutz</a>
            <a href="/agb" data-testid="footer-link-agb" className="hover:text-slate-400 transition-colors">AGB</a>
            <a href="/widerruf" data-testid="footer-link-widerruf" className="hover:text-slate-400 transition-colors">Widerruf</a>
            <a href="/admin" className="hover:text-cyan-300 transition-colors">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
